import { useCallback, useEffect, useRef, useState } from 'react';
import { useTabViewStateStore } from '../store/tabViewStateStore';

const SCROLL_SAVE_THROTTLE_MS = 100;
const SCROLL_POSITION_TOLERANCE_PX = 1;

export function useDomScrollPreservation({
  scrollId,
  isRestoreReady,
  contentRevision,
}: {
  scrollId: string;
  isRestoreReady: boolean;
  contentRevision: number;
}): {
  scrollerRef: (el: HTMLElement | Window | null) => void;
} {
  const scrollerElementRef = useRef<HTMLElement | null>(null);
  const [scrollerReadyTick, setScrollerReadyTick] = useState(0);

  const attemptPendingRestore = useCallback(() => {
    const scrollerElement = scrollerElementRef.current;
    if (!scrollerElement) return;

    const {
      isScrollRestorePending,
      clearScrollRestorePending,
      scrollPositions,
    } = useTabViewStateStore.getState();

    if (!isScrollRestorePending(scrollId)) return;

    const savedPosition = scrollPositions[scrollId];
    if (savedPosition === undefined || savedPosition <= 0) {
      clearScrollRestorePending(scrollId);
      return;
    }

    const maxScrollable = Math.max(
      0,
      scrollerElement.scrollHeight - scrollerElement.clientHeight,
    );
    if (maxScrollable <= 0) return;

    scrollerElement.scrollTop = savedPosition;
    const expectedScrollTop = Math.min(savedPosition, maxScrollable);
    if (
      Math.abs(scrollerElement.scrollTop - expectedScrollTop) <
      SCROLL_POSITION_TOLERANCE_PX
    ) {
      clearScrollRestorePending(scrollId);
    }
  }, [scrollId]);

  const scrollerRef = useCallback(
    (el: HTMLElement | Window | null) => {
      if (el instanceof HTMLElement) {
        scrollerElementRef.current = el;
        setScrollerReadyTick((tick) => tick + 1);
        if (useTabViewStateStore.getState().isScrollRestorePending(scrollId)) {
          attemptPendingRestore();
        }
        return;
      }
      if (el === null) {
        scrollerElementRef.current = null;
        setScrollerReadyTick((tick) => tick + 1);
      }
    },
    [attemptPendingRestore, scrollId],
  );

  useEffect(() => {
    const scrollerElement = scrollerElementRef.current;
    if (!scrollerElement) return;

    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        const { isScrollRestorePending, setScrollPosition } =
          useTabViewStateStore.getState();
        if (isScrollRestorePending(scrollId)) return;
        setScrollPosition(scrollId, scrollerElement.scrollTop);
      }, SCROLL_SAVE_THROTTLE_MS);
    };

    scrollerElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollerElement.removeEventListener('scroll', handleScroll);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [scrollId, contentRevision, scrollerReadyTick]);

  useEffect(() => {
    if (!isRestoreReady) return;

    const { scrollPositions, markScrollRestorePending } =
      useTabViewStateStore.getState();
    const savedPosition = scrollPositions[scrollId];
    if (savedPosition === undefined || savedPosition <= 0) return;

    markScrollRestorePending(scrollId);
    attemptPendingRestore();
  }, [
    attemptPendingRestore,
    isRestoreReady,
    contentRevision,
    scrollId,
    scrollerReadyTick,
  ]);

  useEffect(() => {
    const scrollerElement = scrollerElementRef.current;
    if (!scrollerElement) return;

    const resizeObserver = new ResizeObserver(() => {
      attemptPendingRestore();
    });
    resizeObserver.observe(scrollerElement);
    return () => resizeObserver.disconnect();
  }, [attemptPendingRestore, scrollerReadyTick]);

  useEffect(() => {
    const scrollerElement = scrollerElementRef.current;
    if (!scrollerElement) return;

    const contentElement = scrollerElement.firstElementChild;
    if (!contentElement) return;

    const contentResizeObserver = new ResizeObserver(() => {
      attemptPendingRestore();
    });
    contentResizeObserver.observe(contentElement);

    const mutationObserver = new MutationObserver(() => {
      attemptPendingRestore();
    });
    mutationObserver.observe(contentElement, {
      childList: true,
      subtree: true,
    });

    return () => {
      contentResizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [attemptPendingRestore, scrollerReadyTick, contentRevision]);

  return { scrollerRef };
}
